import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const inputCls =
  "w-full border border-[#D9DDE3] bg-white px-4 py-3 text-[15px] text-[#161616] placeholder:text-[#98a2b3] focus:border-[#F5B700] focus:outline-none min-h-[44px]";

export default function EnquiryForm({ defaultProduct = "", defaultModel = "", source = "quote", compact = false }) {
  const [form, setForm] = useState({
    name: "", company: "", phone: "", email: "",
    product_name: defaultProduct, model: defaultModel, quantity: "", requirement: "",
  });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.requirement.trim()) {
      setState("error");
      setError("Please fill in your name, phone number and requirement.");
      return;
    }
    setState("loading");
    try {
      await api.post("/enquiries", { ...form, source });
      setState("success");
    } catch (err) {
      setState("error");
      setError(formatApiError(err));
    }
  };

  if (state === "success") {
    return (
      <div className="border border-[#18864B] bg-[#f0faf4] p-8 text-center" data-testid="enquiry-success">
        <CheckCircle2 className="mx-auto text-[#18864B]" size={40} aria-hidden="true" />
        <p className="mt-4 font-heading text-xl font-semibold text-[#161616]">
          Thank you. Your enquiry has been submitted.
        </p>
        <p className="mt-2 text-sm text-[#667085]">You can also reach us directly by phone or WhatsApp.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate={false} data-testid="enquiry-form" className="space-y-4">
      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div>
          <label htmlFor={`${source}-name`} className="mb-1.5 block text-sm font-medium text-[#161616]">Name *</label>
          <input id={`${source}-name`} data-testid="enquiry-name" required className={inputCls} value={form.name} onChange={set("name")} autoComplete="name" />
        </div>
        <div>
          <label htmlFor={`${source}-company`} className="mb-1.5 block text-sm font-medium text-[#161616]">Company Name</label>
          <input id={`${source}-company`} data-testid="enquiry-company" className={inputCls} value={form.company} onChange={set("company")} autoComplete="organization" />
        </div>
        <div>
          <label htmlFor={`${source}-phone`} className="mb-1.5 block text-sm font-medium text-[#161616]">Phone *</label>
          <input id={`${source}-phone`} data-testid="enquiry-phone" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} autoComplete="tel" />
        </div>
        <div>
          <label htmlFor={`${source}-email`} className="mb-1.5 block text-sm font-medium text-[#161616]">Email</label>
          <input id={`${source}-email`} data-testid="enquiry-email" type="email" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" />
        </div>
        <div>
          <label htmlFor={`${source}-product`} className="mb-1.5 block text-sm font-medium text-[#161616]">Product Name</label>
          <input id={`${source}-product`} data-testid="enquiry-product" className={inputCls} value={form.product_name} onChange={set("product_name")} />
        </div>
        <div>
          <label htmlFor={`${source}-model`} className="mb-1.5 block text-sm font-medium text-[#161616]">Model Number</label>
          <input id={`${source}-model`} data-testid="enquiry-model" className={inputCls} value={form.model} onChange={set("model")} />
        </div>
        <div>
          <label htmlFor={`${source}-qty`} className="mb-1.5 block text-sm font-medium text-[#161616]">Quantity</label>
          <input id={`${source}-qty`} data-testid="enquiry-quantity" className={inputCls} value={form.quantity} onChange={set("quantity")} />
        </div>
        <div className={compact ? "" : "sm:col-span-1"}>
          <label htmlFor={`${source}-attachment`} className="mb-1.5 block text-sm font-medium text-[#161616]">Optional Attachment</label>
          <input id={`${source}-attachment`} data-testid="enquiry-attachment" type="file" className="w-full text-sm text-[#667085] file:mr-3 file:border-0 file:bg-[#111111] file:px-4 file:py-2.5 file:text-sm file:font-semibold file:text-white" />
        </div>
      </div>
      <div>
        <label htmlFor={`${source}-requirement`} className="mb-1.5 block text-sm font-medium text-[#161616]">Requirement *</label>
        <textarea id={`${source}-requirement`} data-testid="enquiry-requirement" required rows={4} className={inputCls} value={form.requirement} onChange={set("requirement")} placeholder="Describe the product, specification or quantity you need." />
      </div>

      {state === "error" && (
        <p role="alert" data-testid="enquiry-error" className="flex items-center gap-2 border border-[#C62828] bg-[#fdf3f3] px-4 py-3 text-sm font-medium text-[#C62828]">
          <AlertTriangle size={16} aria-hidden="true" /> {error}
        </p>
      )}

      <button
        type="submit"
        disabled={state === "loading"}
        data-testid="enquiry-submit"
        className="flex min-h-[48px] w-full items-center justify-center gap-2 bg-[#F5B700] px-6 py-3.5 text-[15px] font-semibold text-[#111111] transition-colors hover:bg-[#D99C00] disabled:opacity-60 sm:w-auto"
      >
        {state === "loading" && <Loader2 className="animate-spin" size={18} aria-hidden="true" />}
        {state === "loading" ? "Submitting…" : "Request a Quote"}
      </button>
    </form>
  );
}
